// Central config for the self-schedule experiment on the local/metro sites
// (WEBDEV-4187, ported from corporate's src/utils/selfScheduleConfig.ts).
//
// The mechanism — redirecting consult-form CTAs to the /schedule app and
// swapping the on-page consult form for either a hidden cloudamp stub or the
// new on-page self-schedule form — is page-agnostic. To enable it on a new
// page, add that page's path below; Layout's form swap and the CTA interceptor
// pick it up automatically.
//
// Always test membership via isSelfSchedulePath() rather than reading this array
// directly — the predicate normalizes trailing slashes, a raw `.includes()` does not.
//
// Matching is exact (after that normalization), not by prefix. '/lp' covers
// '/lp' and '/lp/' only — NOT '/lp/google' (LpSocialTemplate) and not the
// Builder landing pages created at '/lp/<pageUrl>'. Add those individually if
// the experiment ever needs them.
//
// Starting with '/lp' alone: pages/lp/index.js renders no consult form of its
// own, so the swap is purely Layout's, and the page has just one '#consult'
// link (LHRBenefits) with none in its hero CTAs — so variant '2' there is close
// to an isolated "legacy form vs on-page form" test rather than also diverting
// the page's main conversion path. /specials, by contrast, has '#consult' as
// its hero CTA, so enabling it there changes two things at once.
//
// theme-2023 is shared by 228 metro sites, so every addition here lands
// everywhere at once. Listing a page only ARMS it: nothing changes for a
// visitor until Optimizely sets the variant (and, for the form specifically,
// until ON_PAGE_FORM_ENABLED is set in that site's build env).
export const SELF_SCHEDULE_PATHS = ['/lp'];

// On-page anchors that scroll to the consult form. CTAs targeting any of these
// (on a self-schedule path, while the variant is active) are redirected to
// /schedule instead of scrolling to the now-removed form.
//
// Only '#consult' here — unlike corporate, the local theme has no
// '#googlelp-quote'. Layout renders the single `<span id="consult">` target and
// every CTA in theme-2023 points at it.
export const CONSULT_FORM_ANCHORS = ['#consult'];

// Container id of the on-page self-schedule form. Exported from here and
// rendered by the component itself, so the id the form carries and the id
// anything else looks for cannot drift apart.
export const ON_PAGE_SELF_SCHEDULE_FORM_ID = 'on-page-self-schedule-form';

// Treat "/foo" and "/foo/" as the same path (Gatsby serves trailing slashes).
// Exported so every path comparison in the experiment uses the same rule —
// isSelfSchedulePath below and useSelfScheduleCtaRedirect's same-page check.
export function normalizePath(pathname) {
    return pathname.length > 1 && pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;
}

export function isSelfSchedulePath(pathname) {
    if (typeof pathname !== 'string') return false;
    return SELF_SCHEDULE_PATHS.includes(normalizePath(pathname));
}

// Kill-switch for the on-page form specifically, independent of the Optimizely
// variant — lets us go dark on just the form (e.g. a form-side bug) without a
// campaign change and without touching the self-schedule app. Off (or unset)
// falls back to the hidden stub even under variant '2' (see useSelfScheduleFlags).
//
// A GATSBY_-prefixed env var is inlined at build time, so flipping it requires a
// rebuild, not just a redeploy.
export const ON_PAGE_FORM_ENABLED = process.env.GATSBY_ONPAGE_FORM_ENABLED === 'true';
